import slugify from "@sindresorhus/slugify";
import type {TopicItemDto} from "../dibAppApi/dibAppApi";
import {TOPIC_TYPE_PATHS, isTopicTypePathKey} from "../paths";
import {fileExtensionMap, isValidFileType} from "../features/content/contentUtils";
import type {ResourceType} from "../features/content/types";

export type TopicItemLink = {
    href: string;
    /** Set only for file topics: suggested filename for the download attribute. */
    download?: string;
};

export function getUrlForItem(item: TopicItemDto): TopicItemLink {
    // resourceTypeId arrives as a JSON number (or is absent/null on an old
    // API); ResourceType is string-valued, so convert before the guard.
    // Null/undefined/unknown/non-file all fail the guard → topic path (C4).
    const resourceTypeId =
        item.resourceTypeId == null
            ? undefined
            : (String(item.resourceTypeId) as ResourceType);
    if (isValidFileType(resourceTypeId) && item.id) {
        const extension = fileExtensionMap[resourceTypeId] ?? "";
        // Absolute legacy file URL — ExperimentalLink rewrites it to the
        // same-origin /dibappapi proxy; the absolute shape stays so the
        // href still works if pasted or shared outside the SPA.
        return {
            href: buildLegacyFileUrl(item.id),
            download: `${item.name ?? ""}${extension}`,
        };
    }
    const key = parseInt(item.type ?? "", 10);
    if (isTopicTypePathKey(key)) {
        const topicPart = `/${TOPIC_TYPE_PATHS[key]}/${slugify(item.name ?? "t")}/${encodeURIComponent(item.id?.toLowerCase() ?? "")}`;
        return {href: `${getDibBaseUrl()}${topicPart}`};
    }
    return {href: ""};
}

/**
 * Returns the base URL for the dib app, picking the dev host when running
 * on localhost or ai.dibdev.no.
 */
export function getDibBaseUrl():
    | "https://dib.dibdev.no"
    | "https://app.dib.no" {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "ai.dibdev.no") {
        return "https://dib.dibdev.no";
    }
    return "https://app.dib.no";
}

/**
 * Resolves a backend-emitted content url into an absolute dib app URL. The
 * backend owns the path; this only supplies the origin for host-relative urls.
 *
 * The production host is deliberate rather than getDibBaseUrl() — it matches
 * the reference list under an answer, and toInternalPath accepts it in dev.
 *
 * Returns "" for a missing url instead of a link to ".../undefined".
 */
export function resolveDibHref(url: string | null | undefined): string {
    const trimmed = url?.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    // A leading slash would give "app.dib.no//lov/x", and that double slash
    // stops toInternalPath's result from matching an SPA route.
    return `https://app.dib.no/${trimmed.replace(/^\/+/, "")}`;
}

/**
 * Hosts the dib app is served from. Besides the two vanity hosts, the dev and
 * staging admin APIs emit their App Service hostnames in content links (e.g.
 * the news feed's `link`), so those count as the dib app too — without them
 * every dib link on ai.dibdev.no/staging falls through to a new-tab anchor.
 */
const DIB_APP_HOSTS = new Set([
    "app.dib.no",
    "dib.dibdev.no",
    "app-dib-dev.azurewebsites.net",
    "app-dib-prod-staging.azurewebsites.net",
]);

/**
 * True when a URL points at the dib app: DIB_APP_HOSTS, plus any localhost
 * origin while we ourselves run locally — a locally-run dib app is served on
 * its own port (e.g. https://localhost:44332, the host the news API emits in
 * dev), so the port is deliberately not pinned.
 */
function isDibAppHost(url: URL): boolean {
    if (DIB_APP_HOSTS.has(url.hostname)) return true;
    return (
        window.location.hostname === "localhost" && url.hostname === "localhost"
    );
}

/**
 * Checks whether a URL (full or relative) points at the dib app (isDibAppHost).
 */
export function isDibContentUrl(href: string): boolean {
    return toInternalPath(href) !== null;
}

/**
 * Absolute legacy file-download URL on the dib app host (app.dib.no /
 * dib.dibdev.no). This is the inverse of rewriteFileUrlForExperimental,
 * which converts it to the same-origin /dibappapi/... route for in-app
 * rendering. Kept absolute — the non-destructive shape works against the
 * legacy app if the href escapes the SPA (copied, shared, logged).
 */
export function buildLegacyFileUrl(guid: string): string {
    return `${getDibBaseUrl()}/dibapi/document/file?resourceId=${encodeURIComponent(guid)}`;
}

/**
 * Legacy file URLs emitted by ai-backend always work on app.dib.no
 * (the host they target). The in-app surface is better served by the
 * same-origin /dibappapi/... shape, which the KAI BFF proxies to
 * dib-app-api; ExperimentalLink applies this rewrite unconditionally.
 *
 * Converts (absolute or no-leading-slash relative):
 *   https://app.dib.no/dibapi/document/file?resourceId=<guid>
 *   dibapi/document/file?resourceId=<guid>
 *
 * to:
 *   /dibappapi/api/documents/<guid>/file
 *
 * Returns the input unchanged if it doesn't match the legacy file
 * pattern.
 */
export function rewriteFileUrlForExperimental(href: string): string {
    if (!href) return href;
    const base = getDibBaseUrl();
    let url: URL;
    try {
        url = new URL(href, base);
    } catch {
        return href;
    }
    if (!isDibAppHost(url)) {
        return href;
    }
    // Pathname can be "/dibapi/document/file" (absolute input) or
    // "//dibapi/document/file" (no-leading-slash relative input
    // absolutised by the URL parser preserves the double slash).
    const isLegacyFilePath =
        url.pathname === "/dibapi/document/file" ||
        url.pathname.endsWith("/dibapi/document/file");
    if (!isLegacyFilePath) return href;
    const resourceId = url.searchParams.get("resourceId");
    if (!resourceId) return href;
    return `/dibappapi/api/documents/${encodeURIComponent(resourceId)}/file`;
}

/**
 * Converts any dib app URL (see isDibAppHost) to an internal route path.
 *
 * Accepts:
 *   - Full URLs:    https://app.dib.no/tema/some-slug/some-id
 *   - Relative:     tema/some-slug/some-id
 *
 * Returns e.g. `/tema/some-slug/some-id`, or `null` if the URL
 * does not point at a dib app host.
 */
export function toInternalPath(href: string): string | null {
    if (!href) return null;

    const base = getDibBaseUrl();
    try {
        const url = new URL(href, base);
        if (!isDibAppHost(url)) return null;
        // BFF-routed paths are not SPA routes — let callers fall through to a
        // plain anchor so the browser hits the BFF proxy instead of <Link>
        // (which would trigger "No routes matched location").
        if (
            url.pathname.startsWith("/dibapi/") ||
            url.pathname.startsWith("/dibappapi/")
        ) {
            return null;
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}
